// TrailsPage — dedicated `/trails` route. Features the interactive map at the
// top, a "Adaptive-only" toggle, then a list of trail cards below the map so
// visitors on cursorless devices (thumb-only, screen-reader) can still browse
// the whole set without wrestling with the map.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mountain } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TrailMap from '@/components/TrailMap';
import { TRAILS_DATA, DIFFICULTY_COLOURS } from '@/data/trails';
import { TRAILS } from '@/constants/testIds';

export default function TrailsPage() {
  const [adaptiveOnly, setAdaptiveOnly] = useState(false);
  const [activeSlug, setActiveSlug] = useState(null);
  const visible = adaptiveOnly
    ? TRAILS_DATA.filter((t) => t.adaptiveFriendly)
    : TRAILS_DATA;

  return (
    <div data-testid={TRAILS.page} className="min-h-screen bg-[var(--brand-parchment)]">
      <Navigation />

      <main className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.22em] text-[var(--brand-forest)] hover:text-[var(--brand-olive)] transition-colors mb-6"
          >
            <ArrowLeft size={14} /> Back home
          </Link>

          <div className="max-w-3xl">
            <p className="font-eyebrow mb-3">
              Trails · Curated by the founder
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--brand-forest)] tracking-tight leading-[1.05]">
              Six Scottish rides.<br />
              <span className="italic">Personally scouted, honestly told.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[var(--brand-ink)] leading-relaxed max-w-2xl">
              These aren&rsquo;t Instagram trails, mate. They&rsquo;re the ones the founder actually rides — with a
              gamekeeper&rsquo;s honest notes on when to go, what to bring, and the wee bothy or cafe
              worth stopping at along the way.
            </p>
          </div>

          {/* Filter row */}
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid={TRAILS.filterAll}
              onClick={() => setAdaptiveOnly(false)}
              aria-pressed={!adaptiveOnly}
              className={
                'inline-flex items-center gap-2 border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition-all ' +
                (!adaptiveOnly
                  ? 'bg-[var(--brand-forest)] border-[var(--brand-forest)] text-[var(--brand-parchment)]'
                  : 'border-[var(--brand-forest)] text-[var(--brand-forest)] hover:bg-[var(--brand-forest)]/5')
              }
            >
              All Trails
              <span className="opacity-70">({TRAILS_DATA.length})</span>
            </button>
            <button
              type="button"
              data-testid={TRAILS.filterAdaptive}
              onClick={() => setAdaptiveOnly(true)}
              aria-pressed={adaptiveOnly}
              className={
                'inline-flex items-center gap-2 border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition-all ' +
                (adaptiveOnly
                  ? 'bg-[var(--brand-gold)] border-[var(--brand-gold)] text-[var(--brand-forest-deep)] shadow-[0_0_0_3px_rgba(197,179,88,0.25)]'
                  : 'border-[var(--brand-gold-soft)] text-[var(--brand-forest)] hover:bg-[var(--brand-gold)]/10')
              }
            >
              Adaptive-friendly only
              <span className="opacity-70">({TRAILS_DATA.filter((t) => t.adaptiveFriendly).length})</span>
            </button>
          </div>

          {/* Map */}
          <div className="mt-6">
            <TrailMap
              trails={TRAILS_DATA}
              adaptiveOnly={adaptiveOnly}
              activeSlug={activeSlug}
              onSelect={setActiveSlug}
            />
            <p className="mt-2 text-[10px] uppercase tracking-[0.20em] text-[var(--brand-muted)]">
              Map data © OpenStreetMap contributors · Pins are trailhead approximations
            </p>
          </div>

          {/* Trail cards below the map */}
          <div className="mt-16">
            <h2 className="font-display text-3xl text-[var(--brand-forest)] tracking-tight mb-6">
              The six, one by one
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((t) => (
                <article
                  key={t.slug}
                  data-testid={TRAILS.trailCard(t.slug)}
                  className="product-card p-6 flex flex-col"
                >
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <h3 className="font-display text-xl text-[var(--brand-forest)] tracking-tight leading-tight">
                      {t.name}
                    </h3>
                    <span
                      className="text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5"
                      style={{
                        color: '#F4F1EA',
                        background: DIFFICULTY_COLOURS[t.difficulty] || '#4A5D23',
                      }}
                    >
                      {t.difficulty}
                    </span>
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--brand-muted)] font-semibold mb-3">
                    {t.region}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-[var(--brand-ink)] mb-3">
                    <span className="inline-flex items-center gap-1">
                      <Mountain size={12} className="text-[var(--brand-gold)]" />
                      {t.distance_km} km · {t.climb_m} m climb
                    </span>
                  </div>
                  <p className="text-xs text-[var(--brand-muted)] mb-3 italic">
                    Surface: {t.surface}
                  </p>
                  {t.adaptiveFriendly && (
                    <div className="mb-3 inline-block self-start text-[10px] uppercase tracking-[0.16em] font-semibold border border-[var(--brand-gold-soft)] text-[var(--brand-forest)] bg-[var(--brand-gold)]/10 px-2 py-0.5">
                      Adaptive-friendly
                    </div>
                  )}
                  <p className="text-sm text-[var(--brand-ink)] leading-relaxed italic border-l-2 border-[var(--brand-gold)] pl-3 mb-4">
                    &ldquo;{t.note}&rdquo;
                  </p>
                  <a
                    href={t.external}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto self-start text-xs font-semibold uppercase tracking-[0.18em] text-[var(--brand-forest)] hover:text-[var(--brand-olive)] transition-colors"
                  >
                    Get directions →
                  </a>
                </article>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
